class AdawatLuckyBagsPopup extends HTMLElement {
  static storageKey = 'adawat-lucky-bags-played';
  static ttlMs = 30 * 24 * 60 * 60 * 1000;

  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    if (this.parentElement && this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    this.rtl = document.documentElement.dir === 'rtl';
    this.delayAfterNl = Number(this.dataset.delayAfterNl || 700);
    this.bags = Array.from(this.querySelectorAll('[data-lb-bag]'));
    this.result = this.querySelector('[data-lb-result]');
    this.pctEl = this.querySelector('[data-lb-pct]');
    this.codeEl = this.querySelector('[data-lb-code]');
    this.noteEl = this.querySelector('[data-lb-note]');
    this.shopBtn = this.querySelector('[data-lb-shop]');
    this.played = false;
    this.won = null;

    this.prizes = [
      { pct: 5, code: this.dataset.code5 || 'ADAWAT5' },
      { pct: 10, code: this.dataset.code10 || 'ADAWAT10' },
      { pct: 15, code: this.dataset.code15 || 'ADAWAT15' },
    ].filter((p) => p.code);

    this.querySelectorAll('[data-lb-close], [data-lb-close-backdrop]').forEach((btn) => {
      btn.addEventListener('click', () => this.dismiss(true));
    });
    this.shopBtn?.addEventListener('click', () => this.shopNow());

    this.bags.forEach((bag) => {
      bag.addEventListener('click', () => this.pickBag(bag));
    });

    this._onKey = (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.dismiss(true);
    };
    document.addEventListener('keydown', this._onKey);

    this._onNewsletterClosed = () => this.scheduleOpen('newsletter');
    window.addEventListener('adawat:newsletter-closed', this._onNewsletterClosed);

    if (this.isDesignMode()) {
      this.open();
      return;
    }

    if (this.hasPlayed()) {
      this.hide();
      return;
    }

    /* If newsletter already dismissed / missing, still show after a short wait. */
    window.setTimeout(() => this.maybeOpenWithoutNewsletter(), 1600);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKey);
    window.removeEventListener('adawat:newsletter-closed', this._onNewsletterClosed);
    window.clearTimeout(this._openTimer);
    this.unlockScroll();
  }

  isDesignMode() {
    return Boolean(window.Shopify?.designMode) || document.documentElement.classList.contains('shopify-design-mode');
  }

  hasPlayed() {
    try {
      const raw = window.localStorage.getItem(AdawatLuckyBagsPopup.storageKey);
      if (!raw) return false;
      const ts = Number(raw);
      if (!Number.isFinite(ts)) return true;
      return Date.now() - ts < AdawatLuckyBagsPopup.ttlMs;
    } catch (_) {
      return false;
    }
  }

  persistPlayed() {
    try {
      window.localStorage.setItem(AdawatLuckyBagsPopup.storageKey, String(Date.now()));
    } catch (_) {
      /* ignore */
    }
  }

  newsletterStillVisible() {
    const nl = document.getElementById('AdawatNewsletterPopup');
    return Boolean(nl && nl.classList.contains('is-open'));
  }

  maybeOpenWithoutNewsletter() {
    if (this.hasPlayed() || this.classList.contains('is-open') || this._scheduled) return;
    if (this.newsletterStillVisible()) return;
    const nl = document.getElementById('AdawatNewsletterPopup');
    /* Newsletter never showed (already dismissed or disabled) → open lucky bags. */
    if (!nl || nl.hasAttribute('hidden') || !nl.classList.contains('is-open')) {
      this.scheduleOpen('fallback');
    }
  }

  scheduleOpen(reason) {
    if (this.hasPlayed() || this._scheduled) return;
    this._scheduled = true;
    window.clearTimeout(this._openTimer);
    const wait = reason === 'newsletter' ? this.delayAfterNl : Math.max(400, this.delayAfterNl);
    this._openTimer = window.setTimeout(() => {
      if (this.hasPlayed()) return;
      if (this.newsletterStillVisible()) {
        this._scheduled = false;
        return;
      }
      this.open();
    }, Number.isFinite(wait) ? wait : 700);
  }

  open() {
    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    this.lockScroll();
  }

  dismiss(persist) {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    this.unlockScroll();
    if (persist && (this.played || this.won)) this.persistPlayed();
    window.setTimeout(() => {
      if (!this.classList.contains('is-open')) this.hide();
    }, 280);
  }

  hide() {
    this.setAttribute('hidden', '');
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
  }

  async pickBag(bag) {
    if (this.played || !this.prizes.length) return;
    this.played = true;
    const prize = this.prizes[Math.floor(Math.random() * this.prizes.length)];
    this.won = prize;

    this.classList.add('is-chosen');
    this.bags.forEach((b) => {
      b.disabled = true;
      b.classList.remove('is-chosen');
      if (b === bag) {
        b.classList.add('is-chosen');
        b.classList.remove('is-hidden');
      } else {
        b.classList.add('is-hidden');
      }
    });

    if (this.pctEl) this.pctEl.textContent = `${prize.pct}%`;
    if (this.codeEl) this.codeEl.textContent = prize.code;
    if (this.noteEl) {
      this.noteEl.textContent = this.rtl
        ? `الخصم مفعّل في السلة تحت الإجمالي. كود ${prize.code}.`
        : `Discount is on in your cart under the total. Code ${prize.code}.`;
    }
    if (this.result) this.result.hidden = false;

    this.persistPlayed();
    await this.applyPrize(prize);
  }

  async applyPrize(prize) {
    const attrs = {
      lucky_bags_pct: String(prize.pct),
      lucky_bags_code: prize.code,
    };
    try {
      if (window.AdawatCart?.applyDiscount) {
        await window.AdawatCart.applyDiscount(prize.code, {
          openDrawer: false,
          attributes: attrs,
        });
      } else {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: prize.code, attributes: attrs }),
        });
      }
    } catch (_) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: prize.code, attributes: attrs }),
        });
      } catch (_) {
        /* View cart will re-apply */
      }
    }

    if (this.noteEl) {
      this.noteEl.textContent = this.rtl
        ? `الخصم مفعّل في السلة تحت الإجمالي. كود ${prize.code}.`
        : `Discount is on in your cart under the total. Code ${prize.code}.`;
    }
  }

  async shopNow() {
    const code = this.won?.code;
    if (code && window.AdawatCart?.applyDiscount) {
      try {
        await window.AdawatCart.applyDiscount(code, { openDrawer: true });
        this.dismiss(true);
        return;
      } catch (_) {
        /* fall through */
      }
    }
    if (code) {
      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/collections/all`;
      return;
    }
    this.dismiss(true);
  }

  lockScroll() {
    if (this._locked) return;
    this._locked = true;
    this._scrollY = window.scrollY || window.pageYOffset || 0;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${this._scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    document.documentElement.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (!this._locked) return;
    this._locked = false;
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    document.documentElement.style.overflow = '';
    window.scrollTo(0, this._scrollY || 0);
  }
}

if (!customElements.get('adawat-lucky-bags-popup')) {
  customElements.define('adawat-lucky-bags-popup', AdawatLuckyBagsPopup);
}
